<template>
    <OnBoardingForm :title="$t('Customer Data - On Boarding')" :current-step="1">

        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div class="mb-6">
                <div class="sm:hidden">
                    <label for="tabs" class="sr-only">{{ $t('Select a person') }}</label>
                    <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                    <select id="tabs" name="tabs"
                            class="block w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500">
                        <option v-for="(tab, tabIdx) in form.account_owners" :key="tabIdx"
                                :selected="isActivePerson(tabIdx)">
                            {{ $t('Contracting party :number: ', { number: tabIdx + 1 }) }}{{ nameFilled(tab) ? `${tab.firstname} ${tab.lastname}` : '' }}
                        </option>
                    </select>
                </div>
                <div class="hidden sm:block">
                    <nav class="isolate flex divide-x divide-gray-200 rounded-lg shadow" :aria-label="$t('Tabs')">
                        <a v-for="(tab, tabIdx) in form.account_owners" :key="tabIdx" href="#"
                           @click.prevent="setActivePerson(tabIdx)"
                           :class="[isActivePerson(tabIdx) ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700', tabIdx === 0 ? 'rounded-l-lg' : '', tabIdx === form.account_owners.length - 1 ? 'rounded-r-lg' : '', 'group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-sm font-medium hover:bg-gray-50 focus:z-10', hasAnyErrors('account_owners',tabIdx) ? 'bg-red-100 hover:bg-red-50':'']"
                           :aria-current="isActivePerson(tabIdx) ? 'page' : undefined">
                                <span class="flex items-center">
                                         <span class="flex items-center pr-2">
                                             <span class="pr-2"
                                                                                    v-if="hasAnyErrors('account_owners',tabIdx)">
                                                 <ExclamationCircleIcon
                                             class="h-5 w-5" aria-hidden="true"/>
                                             </span>
                                             {{ $t('Contracting party :number: ', { number: tabIdx + 1 }) }}</span>
                                         <span class="font-bold" v-if="nameFilled(tab)">{{
                                                 tab.firstname
                                             }} {{ tab.lastname }}</span>
                                </span>
                            <span aria-hidden="true"
                                  :class="[isActivePerson(tabIdx) ? 'bg-primary-500' : 'bg-transparent', 'absolute inset-x-0 bottom-0 h-0.5']"/>
                        </a>
                    </nav>
                </div>
            </div>
            <form class="flex flex-col space-y-6" @submit.prevent="submitForm">
                <div class="flex flex-col space-y-6 px-6">
                    <template v-for="(person, personIdx) in form.account_owners">
                        <template v-if="isActivePerson(personIdx)">
                            <FormSection>
                                <template #title>
                                    {{ $t('Information about the contracting party') }}
                                </template>
                                <template #description>

                                </template>

                                <div class="grid gap-6 grid-cols-12">

                                    <div class="col-span-6">
                                        <Label for="firstname" :value="$t('First name(s)')"/>
                                        <JetInput
                                            id="firstname"
                                            v-model="person.firstname"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="firstname"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.firstname`]"
                                                       class="mt-2"/>
                                    </div>

                                    <div class="col-span-6">
                                        <Label for="lastname" :value="$t('Last name')"/>
                                        <JetInput
                                            id="lastname"
                                            v-model="person.lastname"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="lastname"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.lastname`]"
                                                       class="mt-2"/>
                                    </div>

                                    <div class="col-span-12">
                                        <AlertBox>
                                            <div class="flex-1 md:flex md:justify-between">
                                                <p class="font-bold">
                                                    {{ $t('Please enter the full name as shown on your identity document.') }}
                                                </p>
                                            </div>
                                        </AlertBox>
                                    </div>

                                    <div class="col-span-12">
                                        <Label for="tax_domicile" :value="$t('Tax domicile')"/>
                                        <JetInput
                                            id="tax_domicile"
                                            v-model="person.tax_domicile"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="tax_domicile"
                                        />
                                        <JetInputError
                                            :message="form.errors[`account_owners.${personIdx}.tax_domicile`]"
                                            class="mt-2"/>
                                    </div>

                                    <div class="col-span-6">
                                        <Label for="birthdate" :value="$t('Date of birth')"/>
                                        <JetInput
                                            id="birthdate"
                                            v-model="person.birthdate"
                                            type="date"
                                            class="mt-1 block w-full"
                                            autocomplete="city"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.birthdate`]"
                                                       class="mt-2"/>
                                    </div>

                                    <div class="col-span-6">
                                        <Label for="birth_place" :value="$t('Place of birth')"/>
                                        <JetInput
                                            id="birth_place"

                                            v-model="person.birth_place"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="birth_place"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.birth_place`]"
                                                       class="mt-2"/>
                                    </div>

                                    <div class="col-span-12">
                                        <Label for="nationalities" :value="$t('Nationality/ies')"/>
                                        <JetInput
                                            id="nationalities"
                                            v-model="person.nationalities"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="nationalities"
                                        />
                                        <JetInputError
                                            :message="form.errors[`account_owners.${personIdx}.nationalities`]"
                                            class="mt-2"/>
                                    </div>

                                    <div class="col-span-12">
                                        <Label :value="$t('Power of attorney')" class="mb-3"/>
                                        <SimpleRadioGroupOptions :options="poaOptions"
                                                                 containerClasses="flex flex-wrap space-x-3"
                                                                 v-model="person.account_poa"></SimpleRadioGroupOptions>
                                        <JetInputError class="mt-3"
                                                       :message="form.errors[`account_owners.${personIdx}.account_poa`]"></JetInputError>
                                    </div>

                                </div>

                                <div class="flex flex-col space-y-8">

                                </div>
                            </FormSection>

                            <FormSection>
                                <template #title>
                                    {{ $t('Family situation') }}
                                </template>
                                <template #unstyledDescription>
                                    <div class="mt-2 text-sm text-gray-500">
                                        <p class="mb-2">{{ $t('Information about:') }}</p>
                                        <ul class="list-disc list-outside ml-3 space-y-2">
                                            <li>{{ $t('Marital status') }}</li>
                                            <li>{{ $t('Names and dates of birth of family members') }}</li>
                                            <li>{{ $t('Further details if there is a connection between the assets and the family members') }}
                                            </li>
                                        </ul>
                                    </div>
                                </template>
                                <div class="grid gap-6 grid-cols-12">
                                    <div class="col-span-12">
                                        <Label for="family_situation" :value="$t('Family situation')"/>
                                        <JetTextArea
                                            id="family_situation"
                                            v-model="person.family_situation"
                                            class="mt-1 block w-full"
                                            autocomplete="family_situation"
                                            rows="7"
                                        />
                                        <JetInputError
                                            :message="form.errors[`account_owners.${personIdx}.family_situation`]"
                                            class="mt-2"/>
                                    </div>
                                </div>
                            </FormSection>

                            <FormSection>
                                <template #title>
                                    {{ $t('Residence') }}
                                </template>
                                <template #description>
                                    <template v-if="personIdx > 0">
                                        <jet-secondary-button type="button"
                                                              @click.prevent="setAddressFromPerson(person, form.account_owners[0])">
                                            {{ $t('Copy from first contracting party') }}
                                        </jet-secondary-button>
                                    </template>
                                </template>

                                <div class="grid gap-6 grid-cols-12">
                                    <div class="col-span-12">
                                        <Label for="street" :value="$t('Street')"/>
                                        <JetInput
                                            id="street"
                                            v-model="person.street"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="street"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.street`]"
                                                       class="mt-2"/>
                                    </div>

                                    <div class="col-span-6">
                                        <Label for="zip" :value="$t('Postal code')"/>
                                        <JetInput
                                            id="zip"
                                            v-model="person.zip"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="zip"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.zip`]"
                                                       class="mt-2"/>
                                    </div>

                                    <div class="col-span-6">
                                        <Label for="city" :value="$t('City/Town')"/>
                                        <JetInput
                                            id="city"
                                            v-model="person.city"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="city"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.city`]"
                                                       class="mt-2"/>
                                    </div>

                                    <div class="col-span-12">
                                        <Label for="country" :value="$t('Country')"/>
                                        <JetInput
                                            id="country"
                                            v-model="person.country"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="country"
                                        />
                                        <JetInputError :message="form.errors[`account_owners.${personIdx}.country`]"
                                                       class="mt-2"/>
                                    </div>
                                </div>
                            </FormSection>

                            <FormSection>
                                <template #title>
                                    {{ $t('Contact details') }}
                                </template>
                                <template #description>

                                </template>
                                <div class="grid gap-6 grid-cols-12">
                                    <div class="col-span-12">
                                        <Label for="phone_private" :value="$t('Phone')"/>
                                        <JetInput
                                            id="phone_private"
                                            v-model="person.phone_private"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="phone_private"
                                            placeholder="0441234567"
                                        />
                                        <JetInputError
                                            :message="form.errors[`account_owners.${personIdx}.phone_private`]"
                                            class="mt-2"/>
                                    </div>

                                    <div class="col-span-12">
                                        <Label for="phone_mobile" :value="$t('Mobile phone')"/>
                                        <JetInput
                                            id="phone_mobile"
                                            v-model="person.phone_mobile"
                                            type="text"
                                            class="mt-1 block w-full"
                                            autocomplete="phone_mobile"
                                            placeholder="0771234567"
                                        />
                                        <JetInputError
                                            :message="form.errors[`account_owners.${personIdx}.phone_mobile`]"
                                            class="mt-2"/>
                                    </div>
                                </div>
                            </FormSection>

                            <template v-if="personIdx == 0">
                                <FormSection>
                                    <template #title>
                                        {{ $t('Second contracting party') }}
                                    </template>
                                    <template #description>

                                    </template>

                                    <div class="grid gap-6 grid-cols-12">

                                        <div class="col-span-12">
                                            <Label :value="$t('Is a second contracting party desired?')" class="mb-3"/>
                                            <YesNoGroupOptions containerClasses="flex flex-wrap space-x-3"
                                                               v-model="secondPersonNeeded"></YesNoGroupOptions>
                                        </div>

                                    </div>

                                    <div class="flex flex-col space-y-8">

                                    </div>
                                </FormSection>
                            </template>

                        </template>
                    </template>
                </div>
                <div class="flex justify-end">
                    <JetActionMessage :on="form.recentlySuccessful" class="mr-3">
                        {{ $t('Saved.') }}
                    </JetActionMessage>

                    <JetButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        {{ $t('Save & continue') }}
                    </JetButton>
                </div>
            </form>
        </div>

    </OnBoardingForm>
</template>

<script setup>
import {ExclamationCircleIcon} from '@heroicons/vue/outline'
</script>

<script>
import OnBoardingForm from '@/Layouts/OnBoardingForm.vue';
import FormSection from "@/Components/Form/FormSection";
import Label from '@/Components/Form/Label';
import JetButton from "@/Jetstream/Button";
import JetActionMessage from "@/Jetstream/ActionMessage";
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetTextArea from '@/Jetstream/Textarea.vue';
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import {defineComponent} from "vue";
import SimpleRadioGroupOptions from "../../Components/Form/SimpleRadioGroupOptions";
import YesNoGroupOptions from "../../Components/Form/YesNoGroupOptions.vue";
import AlertBox from "../../Components/AlertBox";
import PersonTabsMixin from "../../Mixins/PersonTabsMixin";

export default defineComponent({
    components: {
        AlertBox,
        OnBoardingForm,
        SimpleRadioGroupOptions,
        FormSection,
        JetInputError,
        Label,
        JetButton,
        JetActionMessage,
        JetInput,
        JetTextArea,
        YesNoGroupOptions,
        JetSecondaryButton,
    },
    mixins: [
        PersonTabsMixin,
    ],
    watch: {
        secondPersonNeeded: function (newValue) {
            if (newValue) {
                this.form.account_owners.push(this.getEmptyAccountOwnerLine());
                this.setActivePerson(1);
                this.scrollToTop();
            } else
                this.form.account_owners.splice(1, 1);
        }
    },
    computed: {},
    props: {
        errors: Object,
        accountOwners: Array,
    },
    data() {
        return {
            form: this.$inertia.form({
                account_owners: this.initializeAccountOwners(),
            }),
            secondPersonNeeded: this.accountOwners.length > 1,
            poaOptions: [
                {type: 'single', name: 'Individual'},
                {type: 'collective', name: 'Collective for two'},
            ],
            yesNoOptions: [
                {type: 1, name: 'Yes'},
                {type: 0, name: 'No'},
            ]
        };
    },
    mounted() {
    },
    methods: {
        scrollToTop() {
            window.scrollTo(0, 0);
        },
        setAddressFromPerson(target, source) {
            target.street = source.street;
            target.zip = source.zip;
            target.city = source.city;
            target.country = source.country;
        },
        submitForm() {
            this.form.post(route('onboarding.step1.store'), {
                preserveScroll: false,
                preserveState: true,
                onError: () => {
                  this.handleFormError();
                }
            })
        },

        handleFormError(){
            if (!this.hasAnyErrors('account_owners', this.activePerson))
                this.setActivePerson(this.activePerson == 0 ? 1 : 0);
        },

        initializeAccountOwners() {
            if (!this.accountOwners.length > 0) {
                return [
                    this.getEmptyAccountOwnerLine(),
                ]
            } else {
                let owners = [];
                this.accountOwners.forEach((person, personIdx) => {
                    owners.push({
                        ...this.getEmptyAccountOwnerLine(),
                        ...person,
                    });
                })
                return owners;
            }
        },

        getEmptyAccountOwnerLine() {
            return {
                firstname: null,
                lastname: null,
                tax_domicile: null,
                street: null,
                zip: null,
                city: null,
                country: null,
                birthdate: null,
                birth_place: null,
                nationalities: null,
                account_poa: null,
                family_situation: null,
                phone_private: null,
                phone_mobile: null,
            }
        }
    }

});
</script>
